import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClMenuList from '../ClMenuList';
import ClPopover, { ClPopoverProps } from '../ClPopover';
import { ClModalProps } from '../ClModal';
import { applyClasses } from '../../lib';

interface TransformOrigin {
  horizontal: number | 'left' | 'center' | 'right';
  vertical: number | 'top' | 'center' | 'bottom';
}

type Classes = {
  paper: any;
  list: any;
};

@Component({
  components: {
    ClPopover,
    ClMenuList
  }
})
export default class ClMenu extends Vue {
  /**
   * This is the element that may be used to set the position of the popover.
   */
  @Prop({
    type: [Object as () => Vue, Element]
  })
  public anchorEl!: Vue | Element;

  /**
   * This is the point on the anchor where the popover's
   * `anchorEl` will attach to. This is not used when the
   * anchorReference is 'anchorPosition'.
   *
   * Options:
   * vertical: [top, center, bottom];
   * horizontal: [left, center, right].
   */
  @Prop({
    type: Object as () => TransformOrigin,
    default: () => ({
      vertical: 'top',
      horizontal: 'left'
    })
  })
  readonly anchorOrigin!: TransformOrigin;

  @Prop({
    type: Object as () => Classes,
    default: () => ({
      paper: '',
      list: ''
    })
  })
  readonly classes!: Classes;

  @Prop({
    type: Boolean,
    default: false
  })
  readonly disableAutoFocusItem!: boolean;

  /**
   * If `true`, the modal will be rendered apart from the portal.
   * This is useful when your current component doesnt have a cl-provider.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  readonly disablePortal!: boolean;

  /**
   * Props for cl-modal element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  readonly clMenuListProps!: object;

  @Prop({
    type: Object as () => ClModalProps,
    default: () => ({})
  })
  readonly clModalProps!: ClModalProps;

  @Prop({
    type: Object,
    default: () => ({})
  })
  readonly clPaperProps!: object;

  /**
   * Props for cl-popover element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  readonly clPopoverProps!: ClPopoverProps;

  /**
   * If true, the menu will be visible.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  readonly open!: boolean;

  @Prop({
    type: Object as () => TransformOrigin,
    default: () => ({
      vertical: 'top',
      horizontal: 'left'
    })
  })
  readonly transformOrigin!: TransformOrigin;

  get clMenuListPropsComputed(): object {
    return {
      class: applyClasses(
        'cl-menu__list',
        this.classes.list,
        (this.clMenuListProps as any).class
      ),
      ...this.clMenuListProps
    };
  }

  get clPopoverPropsComputed(): ClPopoverProps {
    return {
      clPaperProps: {
        class: applyClasses(
          'cl-menu__paper',
          this.classes.paper,
          (this.clPaperProps as any).class
        ),
        ...this.clPaperProps
      },
      clModalProps: this.clPopoverProps.clModalProps
    };
  }
}
